import { useSyncExternalStore } from "react";

import type { MultisigEnrollmentSession } from "@/types";

/**
 * A co-signing setup that ended without turning co-signing on, remembered for
 * the rest of the browser session so Settings can say so.
 *
 * The backend serves only the active enrollment session (the GET answers 404
 * once it fails, expires or is cancelled), so the Owner who started the setup
 * would otherwise just see the "Set up" button again with no explanation.
 * While a session is pending this browser remembers it; if it then vanishes
 * without the organization becoming enrolled, the setup did not finish.
 * Cancelling from the wizard forgets the session first, so the Owner's own
 * cancellation raises no banner.
 */
export type EnrollmentOutcome = {
  sessionId: string;
  /** The invited second Owner's name, when the roster knew it. */
  owner2Name: string | null;
  dismissed: boolean;
  recordedAt: string;
};

export type RememberedEnrollment = {
  sessionId: string;
  owner2Name: string | null;
};

const OUTCOME_KEY = "sdp.enrollment.outcome";
const PENDING_KEY = "sdp.enrollment.lastPending";

/** Sessions still on their way to turning co-signing on. */
const PENDING_STATUSES: MultisigEnrollmentSession["status"][] = [
  "PENDING_OWNER1_KEY",
  "PENDING_OWNER2_KEY",
  "READY",
  "SUBMITTING",
];

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

const read = <T>(key: string): T | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

let outcomeCache: { value: EnrollmentOutcome | null } | null = null;

const write = (key: string, value: unknown | null) => {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage can be unavailable (private mode); the banner is a nicety.
  }
  if (key === OUTCOME_KEY) outcomeCache = null;
  notify();
};

export const isPendingEnrollment = (session: MultisigEnrollmentSession | null | undefined) =>
  !!session && PENDING_STATUSES.includes(session.status);

export const getEnrollmentOutcome = (): EnrollmentOutcome | null => {
  if (!outcomeCache) outcomeCache = { value: read<EnrollmentOutcome>(OUTCOME_KEY) };
  return outcomeCache.value;
};

export const rememberPendingEnrollment = (pending: RememberedEnrollment) => {
  const current = read<RememberedEnrollment>(PENDING_KEY);
  if (current?.sessionId === pending.sessionId && current.owner2Name === pending.owner2Name) return;
  write(PENDING_KEY, pending);
};

/** The Owner cancelled it themselves: nothing to explain later. */
export const forgetPendingEnrollment = () => {
  write(PENDING_KEY, null);
};

export const recordEnrollmentOutcome = (pending: RememberedEnrollment) => {
  const current = getEnrollmentOutcome();
  if (current && current.sessionId === pending.sessionId) return;
  write(OUTCOME_KEY, {
    sessionId: pending.sessionId,
    owner2Name: pending.owner2Name,
    dismissed: false,
    recordedAt: new Date().toISOString(),
  } satisfies EnrollmentOutcome);
};

export const dismissEnrollmentOutcome = () => {
  const current = getEnrollmentOutcome();
  if (!current || current.dismissed) return;
  write(OUTCOME_KEY, { ...current, dismissed: true });
};

export const clearEnrollmentOutcome = () => {
  write(OUTCOME_KEY, null);
  write(PENDING_KEY, null);
};

/**
 * Reconcile what the backend serves now with what this browser remembered.
 * A pending session is remembered; a remembered session that is no longer
 * served, while the organization is still not enrolled, is recorded as an
 * unfinished setup. Returns the outcome recorded by this call, if any.
 */
export const trackEnrollmentSession = ({
  session,
  isEnrolled,
  owner2Name,
}: {
  session: MultisigEnrollmentSession | null | undefined;
  isEnrolled: boolean;
  owner2Name?: string | null;
}): EnrollmentOutcome | null => {
  if (isPendingEnrollment(session) && session) {
    rememberPendingEnrollment({ sessionId: session.id, owner2Name: owner2Name ?? null });
    return null;
  }
  const remembered = read<RememberedEnrollment>(PENDING_KEY);
  if (!remembered) return null;
  write(PENDING_KEY, null);
  if (isEnrolled) return null;
  recordEnrollmentOutcome(remembered);
  return getEnrollmentOutcome();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The recorded outcome, re-rendering when it is recorded or dismissed. */
export const useEnrollmentOutcome = (): EnrollmentOutcome | null =>
  useSyncExternalStore(subscribe, getEnrollmentOutcome, () => null);
